import { Tabs } from 'antd'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import FullScreenContainer from '../../components/FullScreenContainer/FullScreenContainer'
import { selectPatientByKey } from '../../state/patient/patientReducer'
import '../../styles/antd/collapseStyle.css'
import { usePatientContext } from './PatientsView'
import {
  generateFullName,
  PatientViewTitle,
  TabEmptyText,
} from './PatientsViewStyle'
import AlertTab from './PatientTabs/AlertTab/AlertTab'
import LiveDataTab from './PatientTabs/LiveDataTab/LiveDataTab'
import PatientPrescriptionTab from './PatientTabs/PatientPrescriptionTab/PatientPrescriptionTab'
import ProfileTab from './PatientTabs/ProfileTab/ProfileTab'
import RecordedEcgTab from './PatientTabs/RecordedEcgTab/RecordedEcgTab'
import ArrhythmiaDataTab from './PatientTabs/ArrhythmiaDataTab/ArrhythmiaDataTab'
import VitalsDataTab from './PatientTabs/RecordedVitalsTab/RecordedVitalsTab'
import SmsLoggerTab from './PatientTabs/SmsLoggerTab/SmsLoggerTab'
import PatternTab from './PatientTabs/PatternTab/PatternTab'
import AddsChartTab from './PatientTabs/AddsChartTab/AddsChartTab'

type patientTabProp = {
  patientKey: string
  status?: string
  size?: 'middle' | 'small' | 'large'
  isDashboard?: boolean
}

type PatientTabContextType = {
  activeKey: string
  size: 'middle' | 'small' | 'large'
}

const PatientTabContext = createContext<PatientTabContextType | undefined>(
  undefined
)

export const usePatientTabContext = (): PatientTabContextType => {
  const context = useContext(PatientTabContext)
  return context!
}

const fontSizes = {
  small: '0.625rem',
  middle: '0.75rem',
  large: '1rem',
}

export function PatientTabs({
  patientKey,
  status = 'Y',
  size = 'middle',
  isDashboard = false,
}: patientTabProp) {
  const { configureAlertViewState, updateViewState } = usePatientContext()
  const [activeKey, setActiveKey] = useState('1')
  const currentPatient = useSelector(selectPatientByKey(patientKey))

  const [scrollHeight, setScrollHeight] = useState(getScrollHeight())
  const tabUseRef = useRef<HTMLDivElement>(null)

  type ItemType = {
    label: string | JSX.Element
    key: string
    title: string | JSX.Element
    children: JSX.Element
  }
  let subTabItems = [
    {
      label: 'Live',
      key: '1',
      title: 'Live Data',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          {' '}
          {patientKey && status === 'Y' ? (
            <LiveDataTab patientKey={patientKey} />
          ) : (
            <TabEmptyText>Patient is Inactive</TabEmptyText>
          )}{' '}
        </div>
      ),
    },
    {
      label: 'Recorded Vitals',
      title: 'Vitals',
      key: '2',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          {' '}
          {patientKey && status === 'Y' ? (
            <VitalsDataTab patientKey={patientKey} />
          ) : (
            <TabEmptyText>Patient is Inactive</TabEmptyText>
          )}{' '}
        </div>
      ),
    },
    {
      label: 'Recorded ECG',
      title: 'Recorded ECG',
      key: '3',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          {' '}
          {patientKey && status === 'Y' ? (
            <RecordedEcgTab patientKey={patientKey} />
          ) : (
            <TabEmptyText>Patient is Inactive</TabEmptyText>
          )}{' '}
        </div>
      ),
    },
    {
      label: 'Arrhythmia',
      title: 'Arrhythmia Recorded Data',
      key: '4',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          {' '}
          {patientKey && status === 'Y' ? (
            <ArrhythmiaDataTab patientKey={patientKey} />
          ) : (
            <TabEmptyText>Patient is Inactive</TabEmptyText>
          )}{' '}
        </div>
      ),
    },
    // NOTE: Uncomment when working on ADDS feature or it's ready to release
    // {
    //   label: 'ADDS',
    //   title: 'ADDS Chart',
    //   key: '3',
    //   children: (
    //     <div style={{ fontSize: fontSizes[size], display: 'none' }}>
    //       {' '}
    //       {patientKey && status === 'Y' ? (
    //         <AddsTab patientKey={patientKey} />
    //       ) : (
    //         <TabEmptyText>Patient is Inactive</TabEmptyText>
    //       )}{' '}
    //     </div>
    //   ),
    // },
    !isDashboard && {
      label: 'Alerts',
      title: 'Alerts',
      key: '5',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          {' '}
          {patientKey && status === 'Y' ? (
            <AlertTab patientKey={patientKey} />
          ) : (
            <TabEmptyText>Patient is Inactive</TabEmptyText>
          )}{' '}
        </div>
      ),
    },
    {
      label: 'Clinical Notes',
      title: 'Clinical Notes',
      key: '6',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          {' '}
          {patientKey && status === 'Y' ? (
            <PatientPrescriptionTab patientKey={patientKey} />
          ) : (
            <TabEmptyText>Patient is Inactive</TabEmptyText>
          )}{' '}
        </div>
      ),
    },
    !isDashboard && {
      label: 'Profile',
      title: 'Profile',
      key: '7',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          {' '}
          {patientKey && status === 'Y' ? (
            <ProfileTab patientKey={patientKey} />
          ) : (
            <TabEmptyText>Patient is Inactive</TabEmptyText>
          )}{' '}
        </div>
      ),
    },
    {
      label: 'Send SMS',
      title: 'Send SMS',
      key: '8',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          {' '}
          {patientKey && status === 'Y' ? (
            <SmsLoggerTab patientKey={patientKey} />
          ) : (
            <TabEmptyText>Patient is Inactive</TabEmptyText>
          )}{' '}
        </div>
      ),
    },
    {
      label: 'ADDS Chart',
      title: 'ADDS Chart',
      key: '9',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          <AddsChartTab
            patientKey={patientKey ?? ''}
            patientName={`${currentPatient?.firstName} ${currentPatient?.lastName}`}
            patientDOB={currentPatient?.dob}
            patientAge={currentPatient?.age}
            doctorName={currentPatient?.assignedDoctor}
          />
        </div>
      ),
    },
    {
      label: 'MEWS Pattern',
      title: 'MEWS Pattern',
      key: '10',
      children: (
        <div style={{ fontSize: fontSizes[size] }}>
          {' '}
          {patientKey && currentPatient && status === 'Y' ? (
            <PatternTab
              patientKey={patientKey}
              patientId={currentPatient.patientId}
              patientFirstName={currentPatient.firstName}
              patientLastName={currentPatient.lastName}
            />
          ) : (
            <TabEmptyText>No pattern available for patient</TabEmptyText>
          )}{' '}
        </div>
      ),
    },
  ]
  const filteredTabs = subTabItems.filter(Boolean) as ItemType[]

  let items = filteredTabs.map((subTabItem) => ({
    ...subTabItem,
    children: (
      <FullScreenContainer
        title={subTabItem.title}
        children={subTabItem.children}
        fullScreenOnlyTitleElements={
          currentPatient ? (
            <PatientViewTitle>
              <div>Patient ID: {currentPatient?.patientId}</div>
              <div>-</div>
              <div>
                {generateFullName(
                  currentPatient.title,
                  currentPatient.firstName,
                  currentPatient.lastName
                )}
              </div>
            </PatientViewTitle>
          ) : (
            <></>
          )
        }
      />
    ),
  }))

  useEffect(() => {
    let height = getScrollHeight()
    setScrollHeight(height)
  }, [])

  function getScrollHeight() {
    let element = document.querySelector(`[data-row-key="${patientKey}"]`)
    return element?.clientHeight ? element.clientHeight + 20 : 0
  }

  useEffect(() => {
    if (updateViewState) setActiveKey('7')
    else if (configureAlertViewState) setActiveKey('5')
  }, [updateViewState, configureAlertViewState])
  useEffect(() => {
    if (tabUseRef.current)
      tabUseRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [activeKey])

  return (
    <PatientTabContext.Provider
      value={{
        activeKey: activeKey,
        size: size,
      }}
    >
      <div style={{ scrollMarginTop: `${scrollHeight}px` }} ref={tabUseRef}>
        <Tabs
          activeKey={activeKey}
          onChange={(activeKey) => {
            setActiveKey(activeKey)
          }}
          size={size}
          type='card'
          defaultActiveKey='1'
          items={items}
        />
      </div>
    </PatientTabContext.Provider>
  )
}
